import { useState, useEffect, useCallback } from 'react';

import { LexOfficeBookingData } from '../../../types';

interface UseBookingDataFormProps {
  bookingData: LexOfficeBookingData;
  isEditing: boolean;
  onSave?: (updatedData: LexOfficeBookingData) => void;
  onEditModeChange: (editing: boolean) => void;
}

interface UseBookingDataFormReturn {
  editedData: LexOfficeBookingData;
  displayData: LexOfficeBookingData;
  updateField: (
    field: keyof LexOfficeBookingData,
    value: LexOfficeBookingData[keyof LexOfficeBookingData]
  ) => void;
  updateLineItem: (index: number, field: string, value: string | number) => void;
  addLineItem: () => void;
  removeLineItem: (index: number) => void;
  handleSave: () => void;
  handleCancel: () => void;
}

/**
 * Custom hook to manage booking data form state and operations
 */
export const useBookingDataForm = ({
  bookingData,
  isEditing,
  onSave,
  onEditModeChange,
}: UseBookingDataFormProps): UseBookingDataFormReturn => {
  const [editedData, setEditedData] =
    useState<LexOfficeBookingData>(bookingData);

  // Update editedData when bookingData changes from parent (only when not editing)
  useEffect(() => {
    if (!isEditing) {
      setEditedData(bookingData);
    }
  }, [bookingData, isEditing]);

  const updateField = useCallback(
    (
      field: keyof LexOfficeBookingData,
      value: LexOfficeBookingData[keyof LexOfficeBookingData]
    ) => {
      setEditedData({ ...editedData, [field]: value });
    },
    [editedData]
  );

  const updateLineItem = useCallback(
    (index: number, field: string, value: string | number) => {
      const updatedItems = [...editedData.voucherItems];
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      setEditedData({ ...editedData, voucherItems: updatedItems });
    },
    [editedData]
  );

  const addLineItem = useCallback(() => {
    const newItem = {
      amount: 0,
      taxAmount: 0,
      taxRatePercent: 0,
      categoryId: '',
    };
    setEditedData({
      ...editedData,
      voucherItems: [...editedData.voucherItems, newItem],
    });
  }, [editedData]);

  const removeLineItem = useCallback(
    (index: number) => {
      const updatedItems = editedData.voucherItems.filter((_, i) => i !== index);
      setEditedData({ ...editedData, voucherItems: updatedItems });
    },
    [editedData]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(editedData);
    }
    onEditModeChange(false);
  }, [editedData, onSave, onEditModeChange]);

  const handleCancel = useCallback(() => {
    setEditedData(bookingData);
    onEditModeChange(false);
  }, [bookingData, onEditModeChange]);

  // Display editedData if we're editing, otherwise show the prop data
  const displayData = isEditing ? editedData : bookingData;

  return {
    editedData,
    displayData,
    updateField,
    updateLineItem,
    addLineItem,
    removeLineItem,
    handleSave,
    handleCancel,
  };
};
